import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import Select from 'react-select';
import DeepEqual from 'fast-deep-equal';
import AsyncSelect from 'react-select/async';
import Geosuggest from '@ubilabs/react-geosuggest';
import Badges from './Badges';
import KeywordSearchInput from '../KeywordSearch';
import SliderSearchInput from '../SliderSearchInput';
import { analyticsTrack } from '../../../analyticsUtils';
import {
  SELECT_INPUTS,
  ASYNC_INPUTS,
  SLIDER_INPUTS,
  GEOFILTER_INPUTS,
  TEXT_INPUTS,
  CHECKBOX_INPUTS,
} from './constants';

class SearchMenuItem extends PureComponent {
  static getOptions(input, submenuTitle) {
    const asyncUrl = submenuTitle === 'Diseases'
      ? '/users/autocomplete_disease_code' : '/languages/autocomplete_dropdown';
    return axios.get(
      asyncUrl,
      { params: { term: input } },
    ).then((response) => {
      if (submenuTitle === 'Languages') {
        return response.data.map((language) => ({
          label: language.text,
          value: language.id,
        }));
      }
      return response.data;
    });
  }

  constructor(props) {
    super(props);
    this.state = {
      isActive: false,
      selections: 0,
    };

    this.renderSubcontent = this.renderSubcontent.bind(this);
    this.handleToggle = this.handleToggle.bind(this);
    this.extractSelectionData = this.extractSelectionData.bind(this);
  }

  componentDidMount() {
    this.extractSelectionData();
  }

  componentDidUpdate(prevProps) {
    const { name, selectionData } = this.props;
    const areSelectionsEqual = DeepEqual(selectionData[name], prevProps.selectionData[name]);

    if (!areSelectionsEqual) this.extractSelectionData();
  }

  handleToggle() {
    const { name } = this.props;
    const { isActive } = this.state;
    this.setState({ isActive: !isActive }, () => {
      if (!isActive) {
        analyticsTrack(`Clicked to open ${name} tab`, { location: 'Search Index' });
      }
    });
  }

  extractSelectionData() {
    const { name, selectionData } = this.props;
    const { isActive } = this.state;
    const selections = Object.entries(selectionData[name]).reduce((prev, curr) => {
      const isCheckbox = typeof curr[1] === 'boolean';
      const isSlider = curr[0] === 'Hourly Rate' || curr[0] === 'Years of Experience';
      const isKeywordOrRole = (curr[0] === 'Keyword' || curr[0] === 'Role');
      if ((isKeywordOrRole && (typeof curr[1] === 'string'))
          || (isCheckbox && curr[1])
          || (isSlider && curr[1].length > 1)
      ) return prev + 1;
      if ((isKeywordOrRole && (typeof curr[1] === 'object')) || (isCheckbox && !curr[1])) return prev;
      return prev + curr[1].length;
    }, 0);

    if (selections > 0) {
      this.setState({
        ...!isActive && {
          isActive: true,
        },
      });
    }

    this.setState({ selections });

    if (name === 'Role') {
      this.setState({ isActive: true });
    }
  }

  renderSubcontent() {
    const {
      name,
      structuralData,
      selectionData,
      handleSelection,
      handleChange,
      setSelectionState,
      registerSliderResetFunction,
    } = this.props;

    return (
      <Fragment>
        {
          Object.entries(structuralData).map(([submenuTitle, options]) => {
            const isSelectInput = SELECT_INPUTS.includes(submenuTitle);
            const isGeoInput = GEOFILTER_INPUTS.includes(submenuTitle);
            const isAsyncInput = ASYNC_INPUTS.includes(submenuTitle);
            const isSliderInput = SLIDER_INPUTS.includes(submenuTitle);
            const isCheckbox = CHECKBOX_INPUTS.includes(submenuTitle);
            const isTextInput = TEXT_INPUTS.includes(submenuTitle);

            if (submenuTitle === 'Keyword') {
              return (
                <KeywordSearchInput
                  key={submenuTitle}
                  name={name}
                  selectionData={selectionData}
                  submenuTitle={submenuTitle}
                  handleChange={handleChange}
                  setSelectionState={setSelectionState}
                />
              );
            }

            if (isSelectInput) {
              const doesInputHaveSelections = selectionData[name][submenuTitle].length > 0;
              return (
                <Fragment key={submenuTitle}>
                  <div className="subcontent-title">{submenuTitle === 'Role' ? 'Search by role' : submenuTitle}</div>
                  <Select
                    placeholder={`Type ${submenuTitle}`}
                    options={options}
                    className="basic-multi-select"
                    classNamePrefix="select"
                    value={doesInputHaveSelections ? selectionData[name][submenuTitle].map(
                      (selection) => options.find((option) => option.label === selection),
                    ) : []}
                    onChange={(val) => handleSelection(name, val, submenuTitle)}
                    isMulti
                  />
                </Fragment>
              );
            }

            if (isAsyncInput) {
              const doesInputHaveSelections = selectionData[name][submenuTitle].length > 0;
              return (
                <Fragment key={submenuTitle}>
                  <div className="subcontent-title">{submenuTitle}</div>
                  <AsyncSelect
                    placeholder={`Search ${submenuTitle}`}
                    loadOptions={(input) => this.constructor.getOptions(input, submenuTitle)}
                    value={doesInputHaveSelections ? selectionData[name][submenuTitle].map(
                      (selection) => ({ label: selection, value: selection }),
                    ) : selectionData[name][submenuTitle]}
                    onChange={(val) => handleSelection(name, val, submenuTitle)}
                    closeOnSelect={false}
                    closeMenuOnSelect={false}
                    onSelectResetsInput={false}
                    className="basic-multi-select"
                    isMulti
                    cacheOptions
                  />
                </Fragment>
              );
            }

            if (isSliderInput) {
              const defaultValue = selectionData[name][submenuTitle].length > 0
                ? selectionData[name][submenuTitle] : options;

              return (
                <SliderSearchInput
                  key={submenuTitle}
                  name={name}
                  submenuTitle={submenuTitle}
                  defaultValue={defaultValue}
                  handleChange={handleChange}
                  options={options}
                  registerSliderResetFunction={registerSliderResetFunction}
                />
              );
            }

            if (isGeoInput) {
              return (
                <Fragment key={submenuTitle}>
                  <div className="subcontent-title">{submenuTitle}</div>
                  <div className="filter-geosuggest">
                    <Geosuggest
                      initialValue={selectionData[name][submenuTitle][0] || ''}
                      inputClassName="form-control"
                      types={['(cities)']}
                      onSuggestSelect={(obj) => handleSelection(name, `${obj.location.lat}, ${obj.location.lng}`, submenuTitle)}
                    />
                  </div>
                </Fragment>
              );
            }

            if (isCheckbox) {
              return (
                <Fragment>
                  <div className="subcontent-title">{submenuTitle}</div>
                  <label
                    className="custom-checkbox-container"
                    htmlFor={submenuTitle}
                  >
                    <input
                      type="checkbox"
                      id={submenuTitle}
                      checked={selectionData[name][submenuTitle]}
                      onChange={() => {
                        if (!selectionData[name][submenuTitle]) {
                          analyticsTrack(`Checked ${submenuTitle}`, { location: 'Search Index' });
                        }
                        handleChange(name, submenuTitle, !selectionData[name][submenuTitle]);
                      }}
                    />
                    <span className="checkmark" />
                    <span className="label filter-text">Available now</span>
                  </label>
                </Fragment>
              );
            }

            if (isTextInput) {
              return (
                <Fragment>
                  <div className="subcontent-title">{submenuTitle}</div>
                  <input
                    type="text"
                    className="form-control"
                    placeholder={`Type ${submenuTitle}s`}
                    value={selectionData[name][submenuTitle] || ''}
                    onChange={(event) => {
                      if (selectionData[name][submenuTitle]
                        && selectionData[name][submenuTitle].length >= 2) {
                        analyticsTrack(`Filtered by ${submenuTitle}`, { location: 'Search Index' });
                      }
                      handleChange(name, submenuTitle, event.target.value);
                    }}
                  />
                </Fragment>
              );
            }

            return (
              <Badges
                key={submenuTitle}
                submenuTitle={submenuTitle}
                menuItemTitle={name}
                options={options}
                selections={selectionData}
                handleSelection={handleSelection}
              />
            );
          })
}
      </Fragment>
    );
  }

  render() {
    const { isActive, selections } = this.state;
    const { name } = this.props;

    return (
      <div className="menu-filter-item">
        <div
          id={name}
          className="item-header"
          role="link"
          tabIndex={0}
          onClick={this.handleToggle}
        >
          <div className="item-title demibold">
            {name}
            {selections > 0 && (
              <span className="normal-weight">{`${selections} Selected`}</span>
            )}
          </div>
          <i className={`fas fa-chevron-${isActive ? 'up' : 'down'}`} />
        </div>
        {
          isActive && (
            <div className="item-content">
              <div className="subcontent">
                {this.renderSubcontent()}
              </div>
            </div>
          )
        }
      </div>
    );
  }
}

SearchMenuItem.propTypes = {
  name: PropTypes.string.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleSelection: PropTypes.func.isRequired,
  structuralData: PropTypes.oneOfType([PropTypes.shape({}),
    PropTypes.array]).isRequired,
  selectionData: PropTypes.shape({}).isRequired,
  setSelectionState: PropTypes.func.isRequired,
  registerSliderResetFunction: PropTypes.func.isRequired,
};

export default SearchMenuItem;
